/**
 * HomePage — raajadharma.org portal hub
 *
 * Shows two featured program panels:
 *   🏹 Dhanurveda  → /rajdharma/dhanurveda
 *   🗣️ Sanskrit Samvad → /samvad
 *
 * + Impact counters in hero
 * + Educational Tourism badges on program cards
 * + Family Educational Trip CTA
 */
import { useEffect, useState } from 'react';
import { useGlossary } from '../contexts/GlossaryContext';
import { latestOfferings } from './ProductsShowcase';
import '../styles/HomePage.css';

interface ImpactSummary {
  programs: number;
}

const IMPACT_FALLBACK: ImpactSummary = { programs: 2 };

interface Props {
  onNavigate: (path: string) => void;
}

export default function HomePage({ onNavigate }: Props) {
  const { t, locale } = useGlossary();
  const [impact, setImpact] = useState<ImpactSummary>(IMPACT_FALLBACK);

  const tl = (key: string, fallback: string) => {
    const v = t(key);
    return v === key ? fallback : v;
  };
  const pick = (value: { hi: string; en: string }) => (locale === 'en' ? value.en : value.hi);

  useEffect(() => {
    const controller = new AbortController();
    fetch('/api/impact/summary', { signal: controller.signal })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setImpact(d); })
      .catch(() => {/* fallback stays */});
    return () => controller.abort();
  }, []);

  return (
    <div className="hp-page">
      {/* ── Portal hero ── */}
      <div className="hp-hero">
        <div className="hp-hero-brand">राजधर्म</div>
        <div className="hp-hero-sub">Raajadharma Gurukul</div>
        <p className="hp-hero-theme">
          {tl('home.theme', 'Securing Sanatani Heritage through Rajadharma')}
        </p>

        {/* Impact counters */}
        <div className="hp-impact-strip">
          <div className="hp-impact-counter">
            <span className="hp-impact-num">{impact.programs}</span>
            <span className="hp-impact-label">{tl('home.impact.programs', 'Programs')}</span>
          </div>
        </div>
      </div>

      {/* ── Latest book, tool, and game ── */}
      <p className="hp-programs-heading">
        {tl('home.latest.heading', 'नवीनतम · Latest')}
      </p>
      <div className="hp-latest">
        {latestOfferings().map((item) => {
          const kindLabel = item.kind === 'book'
            ? tl('home.latest.book', 'पुस्तक · Book')
            : item.kind === 'tool'
              ? tl('home.latest.tool', 'उपकरण · Tool')
              : tl('home.latest.game', 'खेल · Game');
          const title = pick(item.title);
          const description = pick(item.description);
          return (
            <article
              key={item.kind}
              className={`hp-latest-card hp-latest--${item.kind}`}
            >
              <div className="hp-latest-media">
                <span className="hp-latest-badge">🆕 {tl('home.latest.badge', 'नवीनतम')}</span>
                <img src={item.imageUrl} alt={title} />
              </div>
              <p className="hp-latest-kicker">{kindLabel}</p>
              <h2 className="hp-latest-title">{title}</h2>
              <p className="hp-latest-desc">{description}</p>
              <button
                type="button"
                className="hp-latest-cta"
                onClick={() => onNavigate(item.path)}
              >
                {item.kind === 'book'
                  ? tl('home.latest.read', 'पढ़ें →')
                  : item.kind === 'game'
                    ? tl('home.latest.play', 'खेलें →')
                    : tl('home.latest.open', 'खोलें →')}
              </button>
            </article>
          );
        })}
      </div>

      {/* ── Programs heading ── */}
      <p className="hp-programs-heading">
        {tl('home.programs.heading', 'Programs · कार्यक्रम')}
      </p>

      {/* ── Two program panels ── */}
      <div className="hp-programs">
        {/* Sanskrit Samvad — left */}
        <div
          className="hp-program-card hp-program--samvad"
          onClick={() => onNavigate('/samvad')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter') onNavigate('/samvad'); }}
          aria-label={tl('home.samvad.aria', 'संस्कृत युवा संवाद कार्यक्रम')}
        >
          <div className="hp-prog-icon">🗣️</div>
          <div className="hp-prog-program-label">
            {tl('home.samvad.program', 'Sanskrit Conversation')}
          </div>
          <h2 className="hp-prog-title">संस्कृतसंभाषणानुशासनम्</h2>
          <p className="hp-prog-subtitle">
            {tl('home.samvad.subtitle', 'Sanskrit Youth Communication Program · Ages 9–27')}
          </p>
          <p className="hp-prog-desc">
            {tl('home.samvad.desc',
              'पाणिनीय व्याकरण, यास्क-निरुक्ति और वैदिक साहित्य के आधार पर संस्कृत संवाद का '
              + 'अभ्यास। ८ चरण · ४१ गतिविधियाँ · ६ भूमिकाएँ।'
            )}
          </p>
          <div className="hp-prog-meta">
            <span className="hp-prog-pill">🌐 ऑनलाइन + ऑफ़लाइन</span>
            <span className="hp-prog-pill">🎂 आयु ९–२७</span>
            <span className="hp-prog-pill">📖 ४१ गतिविधियाँ</span>
          </div>
          {/* Educational-tourism badge */}
          <div className="hp-prog-badges">
            <span className="trust-badge trust-badge--blue">📜 {tl('home.samvad.badge', 'Reliability through Cultural Rooting')}</span>
          </div>
          <div className="hp-prog-actions">
            <button
              className="hp-prog-cta"
              onClick={(e) => { e.stopPropagation(); onNavigate('/samvad'); }}
            >
              {tl('home.samvad.cta', 'कार्यक्रम देखें →')}
            </button>
            <button
              className="hp-prog-trip-cta"
              onClick={(e) => { e.stopPropagation(); onNavigate('/educational-trips'); }}
            >
              🏕️ {tl('home.trip.cta', 'Family Trip →')}
            </button>
          </div>
        </div>

        {/* Dhanurveda — right */}
        <div
          className="hp-program-card hp-program--dhanurveda"
          onClick={() => onNavigate('/rajdharma/dhanurveda')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter') onNavigate('/rajdharma/dhanurveda'); }}
          aria-label={tl('home.dhanurveda.aria', 'धनुर्वेद — Archery Program')}
        >
          <div className="hp-prog-icon">🏹</div>
          <div className="hp-prog-program-label">
            {tl('home.dhanurveda.program', 'Archery Program')}
          </div>
          <h2 className="hp-prog-title">धनुर्वेद</h2>
          <p className="hp-prog-subtitle">
            {tl('home.dhanurveda.subtitle', 'Traditional Indian Archery · Dharamsala, H.P.')}
          </p>
          <p className="hp-prog-desc">
            {tl('home.dhanurveda.desc',
              'पाणिनीय परम्परा में धनुर्विद्या — लक्ष्य, एकाग्रता और क्षात्र-धर्म का शास्त्रीय अभ्यास। '
              + 'Traditional archery rooted in the Dhanurvedic lineage.'
            )}
          </p>
          <div className="hp-prog-meta">
            <span className="hp-prog-pill">📍 धर्मशाला, हि.प्र.</span>
            <span className="hp-prog-pill">🎂 आयु ९–४५</span>
            <span className="hp-prog-pill">📅 वार्षिक सत्र</span>
          </div>
          <div className="hp-prog-badges">
            <span className="trust-badge">🛡️ {tl('home.dhanurveda.badge', 'Security through Discipline')}</span>
          </div>
          <div className="hp-prog-actions">
            <button
              className="hp-prog-cta"
              onClick={(e) => { e.stopPropagation(); onNavigate('/rajdharma/dhanurveda'); }}
            >
              {tl('home.dhanurveda.cta', 'कार्यक्रम देखें →')}
            </button>
            <button
              className="hp-prog-trip-cta"
              onClick={(e) => { e.stopPropagation(); onNavigate('/educational-trips'); }}
            >
              🏕️ {tl('home.trip.cta', 'Family Trip →')}
            </button>
          </div>
        </div>
      </div>

      {/* ── Quick links ── */}
      <div className="hp-links">
        {[
          { label: tl('home.link.products',    '📚 पुस्तकें'),     path: '/products' },
          { label: tl('home.link.curriculum',  '📜 अभ्यासक्रम'),  path: '/curriculum' },
          { label: tl('home.link.trips',       '🏕️ शैक्षिक यात्रा'), path: '/educational-trips' },
          { label: tl('home.link.impact',      '📊 प्रभाव'),       path: '/impact' },
          { label: tl('home.link.careers',     '🎯 वृत्ति'),       path: '/careers' },
          { label: tl('home.link.about',       'ℹ️ परिचय'),        path: '/about' },
          { label: tl('home.link.contact',     '✉️ संपर्क'),       path: '/contact' },
        ].map(({ label, path }) => (
          <button
            key={path}
            className="hp-link-chip"
            onClick={() => onNavigate(path)}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}
